import { supabase } from './supabaseClient';
import { CustomerOverride, InvoiceCostCenter } from '../types';
import { LastUsedInvoiceDefaults, pickLastUsedInvoiceDefaults } from './invoiceLogic';
import { describeError } from './errorMessage';

/**
 * Kostenstellen des Rechnungskopfs: Vorlagen fuer die BEZEICHNUNG (Tabelle
 * invoice_cost_centers, supabase/invoice_cost_centers.sql) und der Griff nach
 * der zuletzt verwendeten Kombination.
 *
 * Eigenes kleines Modul statt eines Anbaus an invoiceService: Zugriffe auf eine
 * Tabelle, die mit Belegen, Nummernkreisen und Zustandsuebergaengen nichts zu
 * tun hat. Die Kostenstelle selbst — Bezeichnung UND Wert — liegt am Beleg
 * (details.costCenterLabel/costCenter im jsonb) und haengt an dieser Tabelle
 * nicht.
 *
 * Arbeitsteilung, die den Zuschnitt erklaert: wiederkehrend ist die
 * BEZEICHNUNG ("PSP-Element"), verschieden ist der WERT ("4711-02"). Vorlagen
 * gibt es deshalb nur fuer die Bezeichnung; damit der Wert trotzdem nicht bei
 * jedem Beleg neu getippt wird, uebernimmt ein neuer Entwurf die zuletzt
 * verwendete Kombination DESSELBEN PROJEKTS (fetchLastUsedInvoiceDefault).
 *
 * Die Vorlagenliste bleibt dabei global (eine Bezeichnung wie "PSP-Element"
 * ist keine Eigenschaft einer Baustelle), der uebernommene WERT haengt am
 * Projekt. Beides ist bewusst getrennt.
 *
 * Zugriffsstil wie crmService: async, snake_case nur hier, camelCase nach
 * aussen. ZWEI Ausnahmen, beide Absicht: fetchInvoiceCostCenters und
 * fetchLastUsedInvoiceDefaults werfen NICHT (siehe dort).
 */

interface CostCenterRow {
    id: string;
    name: string | null;
}

const mapRow = (row: CostCenterRow): InvoiceCostCenter => ({
    id: row.id,
    name: row.name ?? '',
});

/**
 * Fehlercode eines Supabase-Fehlers, soweit vorhanden.
 *
 * Supabase wirft schlichte Objekte, keine Error-Instanzen (siehe
 * errorMessage.ts) — `e.code` ist deshalb nur ueber einen Cast erreichbar.
 */
const errorCode = (e: unknown): string =>
    typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/**
 * Die Tabelle gibt es noch nicht.
 *
 * PGRST205 meldet PostgREST ("could not find the table in the schema cache"),
 * 42P01 ist der Postgres-Code fuer "undefined_table" — welcher der beiden
 * ankommt, haengt daran, ob PostgREST das Schema schon kennt.
 */
const isMissingTable = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};

/** Ergebnis von fetchInvoiceCostCenters — Liste plus Zustand. */
export interface CostCenterFetchResult {
    templates: InvoiceCostCenter[];
    /**
     * Die Liste konnte nicht geladen werden, und zwar aus einem anderen Grund
     * als "Migration noch nicht gefahren" (RLS, Netz, Zeitablauf).
     *
     * Trennt die beiden Faelle bewusst: eine fehlende Tabelle ist der
     * ANGEKUENDIGTE Zustand vor Migration Nr. 26 und braucht keinen Hinweis —
     * ein Rechtefehler dagegen sieht fuer den Bearbeiter genauso aus wie "es
     * gibt eben noch keine Vorlagen", und dann sucht er den Fehler bei sich.
     */
    unavailable: boolean;
    /** Klartext des Fehlers fuer die Konsole — nicht fuer die Oberflaeche. */
    error?: string;
}

/**
 * Die gespeicherten Bezeichnungen, alphabetisch.
 *
 * Wirft NICHT. Das ist die Zusicherung des Features: solange
 * invoice_cost_centers.sql nicht gefahren ist, existiert die Tabelle nicht.
 * Wuerde der Fehler durchschlagen, risse eine fehlende Migration den
 * Rechnungseditor auf — wegen einer Bequemlichkeit. Die Kostenstelle selbst
 * laesst sich immer von Hand eintragen, sie haengt an keiner Tabelle.
 *
 * Verschluckt wird der Fehler trotzdem nicht: alles ausser der fehlenden
 * Tabelle setzt `unavailable`, und der Picker sagt es dezent an.
 *
 * Schreibende Zugriffe machen das ausdruecklich NICHT: wer auf "speichern"
 * drueckt, muss erfahren, dass nichts gespeichert wurde.
 */
export const fetchInvoiceCostCenters = async (): Promise<CostCenterFetchResult> => {
    try {
        const { data, error } = await supabase
            .from('invoice_cost_centers')
            .select('id, name')
            .order('name');
        if (error) throw error;
        return { templates: (data ?? []).map(mapRow), unavailable: false };
    } catch (e) {
        if (isMissingTable(e)) {
            // Erwarteter Zustand vor Migration Nr. 26 — kein Hinweis, keine
            // Warnung. Die Maske ist ohne Vorlagen vollstaendig bedienbar.
            return { templates: [], unavailable: false };
        }
        const message = describeError(e);
        console.warn('Kostenstellen-Vorlagen konnten nicht geladen werden', e);
        return { templates: [], unavailable: true, error: message };
    }
};

/**
 * Die company_id des angemeldeten Profils.
 *
 * Wortgleich mit erpSteeringService.currentCompanyId — bewusst dupliziert
 * statt importiert, damit dieses kleine Modul nicht am ERP-Modul haengt.
 */
const currentCompanyId = async (): Promise<string> => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) throw new Error('Nicht angemeldet.');

    const { data, error } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .maybeSingle();
    if (error) throw new Error(describeError(error));

    const companyId = (data as { company_id?: string } | null)?.company_id;
    if (!companyId) {
        throw new Error(
            'Das eigene Profil hat keine Firmenzuordnung (company_id). ' +
                'Bitte die Migration companies.sql (Nr. 9) ausführen bzw. das Profil zuordnen.',
        );
    }
    return String(companyId);
};

export const createInvoiceCostCenter = async (
    input: Omit<InvoiceCostCenter, 'id'>,
): Promise<InvoiceCostCenter> => {
    const companyId = await currentCompanyId();
    const { data, error } = await supabase
        .from('invoice_cost_centers')
        .insert([{ company_id: companyId, name: input.name.trim() }])
        .select('id, name')
        .single();
    if (error) {
        // 23505 = unique_violation. Der Unique-Index liegt auf
        // (company_id, lower(trim(name))) und ist genau der Fall, den ein
        // Bearbeiter im Alltag produziert: dieselbe Bezeichnung ein zweites
        // Mal. Der rohe Postgres-Text ("duplicate key value violates unique
        // constraint \"invoice_cost_centers_name_uq\"") sagt ihm nicht, was er
        // aendern soll.
        if (errorCode(error) === '23505') {
            throw new Error('Diese Bezeichnung ist schon vergeben.');
        }
        throw error;
    }
    return mapRow(data as CostCenterRow);
};

export const deleteInvoiceCostCenter = async (id: string): Promise<void> => {
    const { error } = await supabase.from('invoice_cost_centers').delete().eq('id', id);
    if (error) throw error;
};

/**
 * Wie viele Belegzeilen JE PROJEKT geladen werden, um die zuletzt verwendete
 * Kostenstelle zu finden.
 *
 * Die Filterung auf einen nicht leeren Wert laeuft bewusst im Code und nicht in
 * der Abfrage: `details->>'costCenter'` liesse sich zwar in PostgREST
 * ausdruecken, aber der Ausdruck ist nicht null-sicher gegen Leerstrings, und
 * ein paar Zeilen mehr zu laden ist billiger als eine Bedingung, die still
 * daneben greift. Deshalb diese kleine Reserve.
 */
const LAST_USED_RESERVE = 20;

/**
 * Obergrenze ueber alle Projekte zusammen.
 *
 * PostgREST kann nicht "die letzten 20 JE Projekt" — es gibt eine Zeilenzahl
 * fuer die ganze Abfrage. Ohne Deckel zoege ein Abrechnungslauf ueber vierzig
 * Projekte achthundert details-Objekte (jedes mit Einleitung, Schlusstext und
 * Zahlungsbedingungen) fuer eine Handvoll Kostenstellen.
 *
 * Der Deckel kann dazu fuehren, dass ein Projekt mit lange zurueckliegendem
 * letztem Beleg aus dem Fenster faellt. Dann bleibt sein Feld leer — die
 * Richtung, in die dieses Feature scheitern darf. Falsch vorbelegt wird nie.
 */
const LAST_USED_MAX_ROWS = 400;

/**
 * Wonach nachgeschlagen wird: das Projekt — und der Debitor, fuer den der neue
 * Beleg entsteht.
 *
 * Der Kunde gehoert dazu, weil eine der Vorbelegungen den EMPFAENGER bestimmt:
 * die belegbezogene Kundenabweichung darf nur aus Belegen desselben Debitors
 * kommen (invoiceLogic: pickLastUsedCustomerOverrides). Kostenstelle und
 * Projektname bleiben davon unberuehrt und greifen weiterhin rein
 * projektbezogen.
 */
export interface LastUsedDefaultsRequest {
    projectId: string;
    customerId?: string;
}

/**
 * Die Vorbelegungen MEHRERER Projekte in einer Abfrage — fuer den
 * Abrechnungslauf.
 *
 * Muster wie fetchCustomerTerms (invoiceService): der Lauf legt die Entwuerfe
 * sequenziell an; holte sich jeder seine Vorbelegung selbst, kaeme bei vierzig
 * Gruppen ein vierzigfacher Roundtrip zusammen.
 *
 * EINE Abfrage fuer beide Angaben — Kostenstelle und abweichender Projektname
 * stehen im selben details-jsonb derselben Belege. Eine zweite Abfrage fuer den
 * Namen zoege genau dieselben Zeilen noch einmal.
 *
 * Wirft NICHT — siehe fetchLastUsedInvoiceDefault.
 */
export const fetchLastUsedInvoiceDefaults = async (
    requests: LastUsedDefaultsRequest[],
): Promise<Map<string, LastUsedInvoiceDefaults>> => {
    /**
     * Je Projekt der erwartete Debitor.
     *
     * Taucht dasselbe Projekt mit ZWEI verschiedenen Debitoren auf, ist der
     * Empfaenger nicht bestimmbar — dann lieber gar keine Abweichung vorbelegen
     * als die des falschen Kunden. Kostenstelle und Projektname stoert das
     * nicht, sie lesen diese Zuordnung gar nicht.
     */
    const expectedCustomerByProject = new Map<string, string | undefined>();
    for (const req of requests) {
        const projectId = req?.projectId?.trim();
        if (!projectId) continue;
        const customerId = req.customerId?.trim() || undefined;
        expectedCustomerByProject.set(
            projectId,
            expectedCustomerByProject.has(projectId) &&
                expectedCustomerByProject.get(projectId) !== customerId
                ? undefined
                : customerId,
        );
    }
    const ids = [...expectedCustomerByProject.keys()];
    if (!ids.length) return new Map();
    try {
        const { data, error } = await supabase
            .from('invoices')
            .select('primary_project_id, customer_id, details')
            .in('primary_project_id', ids)
            .order('updated_at', { ascending: false })
            .limit(Math.min(ids.length * LAST_USED_RESERVE, LAST_USED_MAX_ROWS));
        if (error) throw error;
        return pickLastUsedInvoiceDefaults(
            (data ?? []).map((row) => {
                const r = row as {
                    primary_project_id?: string | null;
                    customer_id?: string | null;
                    details?: LastUsedDetails | null;
                };
                return {
                    primaryProjectId: r.primary_project_id,
                    customerId: r.customer_id,
                    details: r.details,
                };
            }),
            expectedCustomerByProject,
        );
    } catch (e) {
        console.warn('Zuletzt verwendete Belegvorbelegungen nicht ermittelbar', e);
        return new Map();
    }
};

/**
 * Die Vorbelegungen aus den letzten Belegen EINES PROJEKTS — Kostenstelle
 * (Bezeichnung samt Wert) und abweichender Projektname.
 *
 * Damit belegt ein neuer Entwurf die Angabe vor: wer im Mai fuenfzehn
 * Rechnungen zu derselben Baustelle schreibt, tippt "PSP-Element 4711-02"
 * genau einmal. Der Bearbeiter kann sie in der Maske jederzeit aendern oder
 * leeren — die Vorbelegung ist ein Vorschlag, keine Festlegung.
 *
 * PROJEKTBEZOGEN und nicht global: jeder Auftraggeber fuehrt seine eigene
 * Kostenstelle, und die global zuletzt verwendete gehoerte regelmaessig zu
 * einer fremden Baustelle. Ohne Projekt (Sammelrechnung ueber mehrere) wird
 * deshalb gar nicht erst gefragt: das Feld bleibt leer, statt eine Angabe aus
 * einem anderen Vorgang einzutragen.
 *
 * Quelle sind die BELEGE und nicht ein eigener Merkposten: die Angabe steht
 * ohnehin dort, und ein zweiter Speicherort koennte ihr widersprechen.
 * Massgeblich ist `updated_at` und nicht `created_at` — "zuletzt verwendet"
 * heisst zuletzt angefasst, und die Kostenstelle wird oft erst in den
 * bestehenden Entwurf getippt.
 *
 * Der `customerId` gilt NUR fuer die abweichenden Kundenangaben: sie bestimmen
 * den Empfaenger und duerfen deshalb nicht aus einem Beleg stammen, der an einen
 * anderen Debitor ging (etwa nach einer Kundenzusammenfuehrung). Kostenstelle
 * und Projektname bleiben projektbezogen und kommen auch ohne ihn.
 *
 * Wirft NICHT: eine fehlgeschlagene Bequemlichkeit darf weder das Anlegen
 * eines Entwurfs noch einen ganzen Abrechnungslauf scheitern lassen. Ohne
 * Antwort bleibt das Feld leer — genau wie vor diesem Feature.
 */
export const fetchLastUsedInvoiceDefault = async (
    projectId?: string,
    customerId?: string,
): Promise<LastUsedInvoiceDefaults | null> => {
    const id = projectId?.trim();
    if (!id) return null;
    return (await fetchLastUsedInvoiceDefaults([{ projectId: id, customerId }])).get(id) ?? null;
};

/** Nur die Felder, die fetchLastUsedInvoiceDefaults aus details liest. */
interface LastUsedDetails {
    costCenter?: string;
    costCenterLabel?: string;
    projectNameOverride?: string;
    addressExtra1?: string;
    addressExtra2?: string;
    addressExtra3?: string;
    customerOverride?: CustomerOverride;
}
